export type DeepLinkTarget = { kind: "sso-callback"; loginPath: string | null } | { kind: "navigate"; href: string } | { kind: "ignore" };

// Region-specific hosts that a co.valence.nadia:// deep link may target. Kept
// in sync with settings.DEPLOYMENT_HOSTNAMES; add any new per-deployment host
// here so cross-region links resolve to an absolute URL on the right cluster.
export const VALENCE_APP_HOSTS: ReadonlySet<string> = new Set([
    "coach.valence.co",
    "coach-eu.valence.co",
    "nadia.vanguard.valence.co",
    "nadia.private.valence.co",
    "staging.coach.valencetest.co",
    "staging-eu.coach.valencetest.co",
]);

// Local builds load the app from localhost; production and staging apps must
// never follow a deep link off their cluster onto the device's own localhost.
function isLocalDeepLinkHost(hostname: string): boolean {
    return hostname === "localhost" && globalThis.location?.hostname === "localhost";
}

// Resolve a deep-link URL into an action for the appUrlOpen handler to apply.
// Only the script on chat-start links is forwarded; other query parameters
// and fragments are dropped to avoid ?next= injection on login routes (LAB-904).
export function resolveDeepLinkTarget(rawUrl: string): DeepLinkTarget {
    let parsed: URL;
    try {
        parsed = new URL(rawUrl);
    } catch {
        return { kind: "ignore" };
    }

    if (parsed.host === "sso-callback") {
        const st = parsed.searchParams.get("st");
        let origin = "";
        if (parsed.searchParams.get("external") === "1") {
            const host = parsed.searchParams.get("issuing_host");
            if (parsed.protocol !== "co.valence.nadia:" || !host || !VALENCE_APP_HOSTS.has(host)) {
                return { kind: "ignore" };
            }
            origin = `https://${host}`;
        }
        return {
            kind: "sso-callback",
            loginPath: st ? `${origin}/accounts/st/login?st=${encodeURIComponent(st)}&mobile=1&sso=1` : null,
        };
    }

    const isLocal = isLocalDeepLinkHost(parsed.hostname);
    if (isLocal || VALENCE_APP_HOSTS.has(parsed.hostname)) {
        const script = parsed.pathname === "/coach/start/" ? parsed.searchParams.get("script") : null;
        const query = script ? `?${new URLSearchParams({ script })}` : "";
        const origin = isLocal ? `http://${parsed.host}` : `https://${parsed.hostname}`;
        return { kind: "navigate", href: `${origin}${parsed.pathname}${query}` };
    }

    return { kind: "ignore" };
}

// Only external SSO opts into cold-start recovery; leave existing deep links alone.
export function isExternalSsoCallback(rawUrl: string): boolean {
    try {
        const url = new URL(rawUrl);
        return url.protocol === "co.valence.nadia:" && url.host === "sso-callback" && url.searchParams.get("external") === "1";
    } catch {
        return false;
    }
}
